/**
 * Smart Action step action: the machine-readable action a step carries beside
 * its text, so a screen can run it in one click ("Take action").
 *
 * ONE home for the platform's Zod (the `SmartActionStep.action` JSONB column,
 * the detail DTO) and the webapp's switch on `kind` (coding.md Rule 14).
 * Rules-only templates and the mapper write it; the AI never writes the
 * payload itself.
 *
 * Plan: docs/plans/smart-action/phase-11-whole-lifecycle.md § 5.5
 */

import * as z from "zod";

export const smartActionStepActionSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("schedule_visit"),
    visitType: z.enum(["ONSITE", "VIRTUAL"]),
    technicians: z
      .array(
        z.object({
          userId: z.string(),
          fromDateTime: z.iso.datetime({ offset: true }),
          toDateTime: z.iso.datetime({ offset: true }),
        }),
      )
      .min(1),
    /** Minutes, as the visit form's arrival window. */
    arrivalWindow: z.number().int().min(0),
    instructionHint: z.string().nullable(),
    suggestionRank: z.number().int().min(1).max(3).nullable(),
  }),
  z.object({ kind: z.literal("request_feedback") }),
  z.object({
    kind: z.literal("move_status"),
    statusKey: z.enum(["billing", "done"]),
  }),
  z.object({
    kind: z.literal("record_revenue"),
    title: z.string(),
    totalCents: z.number().int().nonnegative().nullable(),
  }),
  z.object({ kind: z.literal("open_invoice"), invoiceId: z.string() }),
]);
export type SmartActionStepAction = z.infer<typeof smartActionStepActionSchema>;
export type SmartActionStepActionKind = SmartActionStepAction["kind"];

export const SMART_ACTION_STEP_ACTION_META: Record<
  SmartActionStepActionKind,
  { label: string }
> = {
  schedule_visit: { label: "Book this visit" },
  request_feedback: { label: "Ask for feedback" },
  move_status: { label: "Move to Billing" },
  record_revenue: { label: "Record the revenue" },
  open_invoice: { label: "Open the invoice" },
};

/** The Take action button's label; a move to `done` reads "Close the order". */
export function stepActionLabel(action: SmartActionStepAction): string {
  if (action.kind === "move_status" && action.statusKey === "done") {
    return "Close the order";
  }
  return SMART_ACTION_STEP_ACTION_META[action.kind].label;
}
